import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Helmet } from 'react-helmet-async';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mojpnneg';

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [showAuditForm, setShowAuditForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [auditForm, setAuditForm] = useState({
    name: '',
    brand_name: '',
    phone: '',
    email: '',
    monthly_revenue: '',
    platform: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (searchParams.get('audit') === 'true') {
      setShowAuditForm(true);
    }
  }, [searchParams]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: 'New Contact Inquiry - Obiquiste Solutions',
          name: contactForm.name,
          email: contactForm.email,
          phone: contactForm.phone || 'Not provided',
          message: contactForm.message,
          form_type: 'Contact Form'
        })
      });
      
      if (response.ok) {
        toast.success('Message sent! We\'ll get back to you soon.');
        setContactForm({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAuditSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: 'New Audit Request - Obiquiste Solutions',
          name: auditForm.name,
          brand_name: auditForm.brand_name,
          phone: auditForm.phone,
          email: auditForm.email,
          monthly_revenue: auditForm.monthly_revenue,
          platform: auditForm.platform,
          form_type: 'Audit Request'
        })
      });
      
      if (response.ok) {
        toast.success('Audit request submitted! We\'ll contact you soon.');
        setAuditForm({
          name: '',
          brand_name: '',
          phone: '',
          email: '',
          monthly_revenue: '',
          platform: '',
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24">
      <Helmet>
        <title>Contact Obiquiste Solutions | Get Free E-commerce Consultation</title>
        <meta name="description" content="Contact us for Amazon, Flipkart & quick commerce services. Based in Faridabad, serving brands across India. Call or email for a free consultation today." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/contact" />
      </Helmet>
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 to-blue-50/50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 data-testid="contact-heading" className="font-heading text-5xl md:text-7xl font-bold tracking-tight mb-6">
              Get in <span className="text-gradient">Touch</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600">
              Let's discuss how we can help you scale your e-commerce brand.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1">
              <h2 className="font-heading text-3xl font-semibold mb-8">Contact Information</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Phone</h3>
                    <a href="tel:8376008909" className="text-slate-600 hover:text-primary">
                      8376008909
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Email</h3>
                    <a
                      href="mailto:info@obiquistesolutions.com"
                      className="text-slate-600 hover:text-primary"
                    >
                      info@obiquistesolutions.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="font-heading text-xl font-semibold mb-4">Business Hours</h3>
                <div className="space-y-2 text-slate-600">
                  <p>Monday - Friday: 9:00 AM - 7:00 PM</p>
                  <p>Saturday: 10:00 AM - 5:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="flex space-x-4 mb-8">
                <button
                  onClick={() => setShowAuditForm(false)}
                  data-testid="tab-contact-form"
                  className={`px-6 py-3 rounded-full font-semibold transition-all ${
                    !showAuditForm
                      ? 'bg-primary text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Contact Form
                </button>
                <button
                  onClick={() => setShowAuditForm(true)}
                  data-testid="tab-audit-form"
                  className={`px-6 py-3 rounded-full font-semibold transition-all ${
                    showAuditForm
                      ? 'bg-primary text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Request Audit
                </button>
              </div>

              {!showAuditForm ? (
                <form
                  onSubmit={handleContactSubmit}
                  data-testid="contact-form"
                  className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200"
                >
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="contact-name">Your Name *</Label>
                      <Input
                        id="contact-name"
                        data-testid="contact-form-name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-email">Email *</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        data-testid="contact-form-email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-phone">Phone</Label>
                      <Input
                        id="contact-phone"
                        type="tel"
                        data-testid="contact-form-phone"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-message">Message *</Label>
                      <Textarea
                        id="contact-message"
                        data-testid="contact-form-message"
                        value={contactForm.message}
                        onChange={(e) =>
                          setContactForm({ ...contactForm, message: e.target.value })
                        }
                        required
                        rows={6}
                        className="mt-2"
                      />
                    </div>
                    <Button
                      type="submit"
                      data-testid="contact-form-submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary text-white hover:bg-primary-600 py-6 rounded-full text-base font-semibold"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send className="ml-2" size={18} />
                    </Button>
                  </div>
                </form>
              ) : (
                <form
                  onSubmit={handleAuditSubmit}
                  data-testid="contact-audit-form"
                  className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="audit-name">Your Name *</Label>
                      <Input
                        id="audit-name"
                        data-testid="contact-audit-name"
                        value={auditForm.name}
                        onChange={(e) => setAuditForm({ ...auditForm, name: e.target.value })}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="audit-brand">Brand Name *</Label>
                      <Input
                        id="audit-brand"
                        data-testid="contact-audit-brand"
                        value={auditForm.brand_name}
                        onChange={(e) =>
                          setAuditForm({ ...auditForm, brand_name: e.target.value })
                        }
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="audit-phone">Phone *</Label>
                      <Input
                        id="audit-phone"
                        type="tel"
                        data-testid="contact-audit-phone"
                        value={auditForm.phone}
                        onChange={(e) => setAuditForm({ ...auditForm, phone: e.target.value })}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="audit-email">Email *</Label>
                      <Input
                        id="audit-email"
                        type="email"
                        data-testid="contact-audit-email"
                        value={auditForm.email}
                        onChange={(e) => setAuditForm({ ...auditForm, email: e.target.value })}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="audit-revenue">Monthly Revenue *</Label>
                      <Select
                        value={auditForm.monthly_revenue}
                        onValueChange={(value) =>
                          setAuditForm({ ...auditForm, monthly_revenue: value })
                        }
                      >
                        <SelectTrigger data-testid="contact-audit-revenue" className="mt-2">
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0-5L">0 - 5 Lakhs</SelectItem>
                          <SelectItem value="5-10L">5 - 10 Lakhs</SelectItem>
                          <SelectItem value="10-25L">10 - 25 Lakhs</SelectItem>
                          <SelectItem value="25-50L">25 - 50 Lakhs</SelectItem>
                          <SelectItem value="50L+">50 Lakhs+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="audit-platform">Platform Selling On *</Label>
                      <Select
                        value={auditForm.platform}
                        onValueChange={(value) => setAuditForm({ ...auditForm, platform: value })}
                      >
                        <SelectTrigger data-testid="contact-audit-platform" className="mt-2">
                          <SelectValue placeholder="Select platform" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Amazon">Amazon</SelectItem>
                          <SelectItem value="Flipkart">Flipkart</SelectItem>
                          <SelectItem value="Myntra">Myntra</SelectItem>
                          <SelectItem value="Meesho">Meesho</SelectItem>
                          <SelectItem value="Multiple">Multiple Platforms</SelectItem>
                          <SelectItem value="None">Not Selling Yet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    data-testid="contact-audit-submit"
                    disabled={isSubmitting}
                    className="w-full mt-6 bg-primary text-white hover:bg-primary-600 py-6 rounded-full text-base font-semibold"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Free Audit'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Visit Our Office
            </h2>
            <div className="flex items-center justify-center text-slate-600">
              <MapPin className="w-5 h-5 text-primary mr-2" />
              <p className="text-lg">5A-13 BP, 2nd Floor, New Industrial Township, Faridabad, Haryana 121001</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200"
          >
            <iframe 
              src="https://www.google.com/maps?q=Obiquiste+Solutions+Faridabad&output=embed" 
              width="100%" 
              height="450" 
              style={{ border: 0, borderRadius: '16px' }}
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Obiquiste Solutions Office Location"
              className="w-full"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-8 text-center"
          >
            <a 
              href="https://maps.google.com/?q=Obiquiste+Solutions+Faridabad" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-primary hover:text-primary-600 font-semibold"
            >
              <MapPin className="w-5 h-5 mr-2" />
              Get Directions on Google Maps
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;